import ReactGA from "react-ga4";

/**
 * Centralized Google Analytics 4 tracking utility.
 * All events are organized by category for easy filtering in GA4.
 *
 * Categories:
 *  - CTA            : Call-to-action button clicks
 *  - Navigation     : Menu, footer, internal link clicks
 *  - Article        : Library content engagement
 *  - Quiz           : Dosha quiz interactions
 *  - Form           : Contact, waitlist, subscription forms
 *  - Auth           : Login, signup events
 *  - Subscription   : Pricing plan interactions
 *  - Social         : Social media & app store clicks
 */

// ─── CTA Buttons ─────────────────────────────────────────────────────────────

export const trackCTAClick = (label, location = "unknown") => {
  ReactGA.event({
    category: "CTA",
    action: "Button Click",
    label: `${label} - ${location}`,
  });
};

// ─── Navigation ──────────────────────────────────────────────────────────────

export const trackNavClick = (label) => {
  ReactGA.event({
    category: "Navigation",
    action: "Menu Click",
    label,
  });
};

export const trackFooterClick = (label) => {
  ReactGA.event({
    category: "Navigation",
    action: "Footer Click",
    label,
  });
};

// ─── Article / Library ───────────────────────────────────────────────────────

export const trackArticleRead = (title, category = "unknown") => {
  ReactGA.event({
    category: "Article",
    action: "Article Read",
    label: title,
  });
  // Also send as GA4 recommended event
  ReactGA.event("view_item", {
    item_list_name: category,
    items: [{ item_name: title, item_category: category }],
  });
};

export const trackArticleCategoryView = (category) => {
  ReactGA.event({
    category: "Article",
    action: "Category Viewed",
    label: category,
  });
};

export const trackLibraryViewAll = (category) => {
  ReactGA.event({
    category: "Article",
    action: "View All Clicked",
    label: category,
  });
};

export const trackReadMoreClick = (title) => {
  ReactGA.event({
    category: "Article",
    action: "Read More Clicked",
    label: title,
  });
};

// ─── Dosha Quiz ──────────────────────────────────────────────────────────────

export const trackQuizStart = () => {
  ReactGA.event({
    category: "Quiz",
    action: "Quiz Started",
    label: "Dosha Quiz",
  });
};

export const trackQuizComplete = (dominantDosha) => {
  ReactGA.event({
    category: "Quiz",
    action: "Quiz Completed",
    label: dominantDosha,
  });
};

export const trackQuizAnswer = (questionIndex, answer) => {
  ReactGA.event({
    category: "Quiz",
    action: "Question Answered",
    label: `Q${questionIndex + 1}: ${answer}`,
  });
};

// ─── Forms ───────────────────────────────────────────────────────────────────

export const trackNewsletterSubscribe = (success) => {
  ReactGA.event({
    category: "Form",
    action: success ? "Newsletter Subscribe Success" : "Newsletter Subscribe Failed",
    label: "Email Newsletter",
  });
};

export const trackContactFormSubmit = (reason, success) => {
  ReactGA.event({
    category: "Form",
    action: success ? "Contact Form Submitted" : "Contact Form Failed",
    label: reason || "General",
  });
};

export const trackDemoRequest = (success) => {
  ReactGA.event({
    category: "Form",
    action: success ? "Demo Request Submitted" : "Demo Request Failed",
    label: "Business Demo",
  });
};

export const trackAffiliateRequest = (success) => {
  ReactGA.event({
    category: "Form",
    action: success ? "Affiliate Request Submitted" : "Affiliate Request Failed",
    label: "Affiliate Program",
  });
};

// ─── Auth ────────────────────────────────────────────────────────────────────

export const trackLogin = (method = "email") => {
  ReactGA.event("login", { method });
};

export const trackSignup = (method = "email") => {
  ReactGA.event("sign_up", { method });
};

export const trackLogout = () => {
  ReactGA.event({
    category: "Auth",
    action: "Logout",
    label: "User Logged Out",
  });
};

// ─── Subscription / Pricing ──────────────────────────────────────────────────

export const trackPlanView = (planName) => {
  ReactGA.event({
    category: "Subscription",
    action: "Plan Viewed",
    label: planName,
  });
};

export const trackPlanSelect = (planName, price) => {
  ReactGA.event("select_item", {
    item_list_name: "Subscription Plans",
    items: [{ item_name: planName, price }],
  });
  ReactGA.event({
    category: "Subscription",
    action: "Plan Selected",
    label: `${planName} - $${price}`,
  });
};

export const trackPurchaseStart = (planName) => {
  ReactGA.event("begin_checkout", {
    items: [{ item_name: planName }],
  });
};

// ─── Social & App Store ──────────────────────────────────────────────────────

export const trackSocialClick = (platform) => {
  ReactGA.event({
    category: "Social",
    action: "Social Media Click",
    label: platform,
  });
};

export const trackAppStoreClick = (store) => {
  ReactGA.event({
    category: "Social",
    action: "App Store Click",
    label: store,
  });
};

// ─── Herb / Explorer ─────────────────────────────────────────────────────────

export const trackHerbView = (herbName) => {
  ReactGA.event({
    category: "Explorer",
    action: "Herb Viewed",
    label: herbName,
  });
};

// ─── Enrollment Funnel ───────────────────────────────────────────────────────

/**
 * Track each step of the onboarding questionnaire.
 * This lets you see where users drop off in the funnel.
 *
 * Usage: trackEnrollmentStep("Age", "30s")
 */
export const trackEnrollmentStep = (stepName, answer = null) => {
  ReactGA.event({
    category: "Enrollment",
    action: `Step: ${stepName}`,
    label: answer || stepName,
  });
};

/**
 * Track when the enrollment questionnaire is fully completed.
 */
export const trackEnrollmentComplete = (heardFrom) => {
  ReactGA.event({
    category: "Enrollment",
    action: "Enrollment Completed",
    label: heardFrom || "Unknown",
  });
  // GA4 standard event for funnel tracking
  ReactGA.event("tutorial_complete");
};
